import { ArrowDownIcon, ArrowUpDownIcon, ArrowUpIcon, Loader2Icon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';

import { columnAriaSort } from '@/lib/a11y';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { MaintenanceStatusBadge } from '@/products/maintenance/features/maintenance-status-badge';
import type { MaintenanceStatusInput } from '@/products/maintenance/features/maintenance-status';

export type MaintenanceRecordColumn<Row> = {
  id: string;
  header: string;
  cell: (row: Row) => ReactNode;
  sortKey?: string;
};

export type MaintenanceRecordListSort = {
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  onSort: (sortBy: string) => void;
};

function MaintenanceColumnSortIcon({ active, order }: { active: boolean; order: 'asc' | 'desc' }) {
  if (!active) {
    return <ArrowUpDownIcon className="size-3.5 text-muted-foreground" aria-hidden />;
  }
  return order === 'asc' ? (
    <ArrowUpIcon className="size-3.5" aria-hidden />
  ) : (
    <ArrowDownIcon className="size-3.5" aria-hidden />
  );
}

function SortableTableHead({
  label,
  sortKey,
  sort,
}: {
  label: string;
  sortKey: string;
  sort: MaintenanceRecordListSort;
}) {
  const active = sort.sortBy === sortKey;
  return (
    <TableHead aria-sort={columnAriaSort(sortKey, sort.sortBy, sort.sortOrder)}>
      <button
        type="button"
        className="relative z-10 inline-flex items-center gap-1 font-medium hover:text-foreground"
        onClick={() => sort.onSort(sortKey)}
      >
        {label}
        <MaintenanceColumnSortIcon active={active} order={sort.sortOrder} />
      </button>
    </TableHead>
  );
}

function MobileSortBar({
  titleHeader,
  titleSortKey,
  statusSortKey,
  columns,
  sort,
}: {
  titleHeader: string;
  titleSortKey?: string;
  statusSortKey?: string;
  columns: readonly { id: string; header: string; sortKey?: string }[];
  sort: MaintenanceRecordListSort;
}) {
  const fields = [
    ...(titleSortKey ? [{ id: titleSortKey, header: titleHeader, sortKey: titleSortKey }] : []),
    ...columns.filter((column) => column.sortKey),
    ...(statusSortKey ? [{ id: statusSortKey, header: 'Status', sortKey: statusSortKey }] : []),
  ];
  if (fields.length === 0) {
    return null;
  }
  return (
    <div className="flex flex-wrap gap-2 md:hidden">
      {fields.map((field) => {
        const sortKey = field.sortKey;
        if (!sortKey) {
          return null;
        }
        const active = sort.sortBy === sortKey;
        return (
          <button
            key={field.id}
            type="button"
            className={`inline-flex min-h-11 items-center gap-1 rounded-full border px-3 text-sm ${
              active ? 'border-primary bg-primary/10 font-medium text-foreground' : 'bg-background'
            }`}
            aria-pressed={active}
            onClick={() => sort.onSort(sortKey)}
          >
            {field.header}
            <MaintenanceColumnSortIcon active={active} order={sort.sortOrder} />
          </button>
        );
      })}
    </div>
  );
}

function pendingMessage(pending: boolean | string | undefined): string | null {
  if (pending === true) {
    return 'Wird geladen …';
  }
  if (typeof pending === 'string' && pending.length > 0) {
    return pending;
  }
  return null;
}

function errorMessage(error: boolean | string | undefined): string | null {
  if (error === true) {
    return 'Die Liste konnte nicht geladen werden.';
  }
  if (typeof error === 'string' && error.length > 0) {
    return error;
  }
  return null;
}

export function MaintenanceRecordList<Row>({
  rows,
  getRowId,
  getHref,
  getTitle,
  getStatus,
  columns,
  titleHeader = 'Name',
  titleSortKey,
  statusSortKey,
  sort,
  pending,
  error,
  emptyLabel,
  filtered = false,
  onReset,
  renderActions,
  renderTitleExtra,
  isRowArchived,
  selection,
}: {
  rows: readonly Row[];
  getRowId: (row: Row) => string;
  getHref: (row: Row) => string;
  getTitle: (row: Row) => string;
  getMeta?: (row: Row) => string | null | undefined;
  getStatus?: (row: Row) => MaintenanceStatusInput;
  columns: readonly MaintenanceRecordColumn<Row>[];
  titleHeader?: string;
  titleSortKey?: string;
  statusSortKey?: string;
  sort?: MaintenanceRecordListSort;
  pending?: boolean | string;
  error?: boolean | string;
  emptyLabel?: string;
  filtered?: boolean;
  onReset?: () => void;
  renderActions?: (row: Row) => ReactNode;
  renderTitleExtra?: (row: Row) => ReactNode;
  /** De-emphasize row (e.g. archived items shown alongside active ones). */
  isRowArchived?: (row: Row) => boolean;
  selection?: {
    selectedIds: ReadonlySet<string>;
    onToggle: (id: string, selected: boolean) => void;
    onToggleAll: (selected: boolean) => void;
  };
}) {
  if (rows.length === 0) {
    const loading = pendingMessage(pending);
    if (loading) {
      return (
        <Card className="items-center justify-center gap-0 py-12 shadow-sm">
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2Icon className="size-4 animate-spin" aria-hidden />
            {loading}
          </p>
        </Card>
      );
    }
    const failure = errorMessage(error);
    if (failure) {
      return (
        <Card className="items-center justify-center gap-0 py-12 shadow-sm">
          <p role="alert" className="text-sm text-destructive">
            {failure}
          </p>
        </Card>
      );
    }
    if (emptyLabel) {
      return (
        <Card className="items-center gap-1 border-dashed py-12 text-center shadow-sm">
          <p className="text-sm font-medium">{emptyLabel}</p>
          {filtered && onReset ? (
            <Button type="button" variant="link" size="sm" onClick={onReset}>
              Filter zurücksetzen
            </Button>
          ) : null}
        </Card>
      );
    }
    return null;
  }

  const allSelected = selection
    ? rows.every((row) => selection.selectedIds.has(getRowId(row)))
    : false;
  const someSelected = selection
    ? rows.some((row) => selection.selectedIds.has(getRowId(row)))
    : false;

  return (
    <div className="space-y-3">
      {sort ? (
        <MobileSortBar
          titleHeader={titleHeader}
          titleSortKey={titleSortKey}
          statusSortKey={statusSortKey}
          columns={columns}
          sort={sort}
        />
      ) : null}
      <ul className="grid gap-3 md:hidden">
        {rows.map((row) => {
          const status = getStatus?.(row);
          const rowId = getRowId(row);
          const archived = isRowArchived?.(row) === true;
          const titleExtra = renderTitleExtra?.(row);
          return (
            <li key={rowId}>
              <Card
                className={`gap-0 overflow-hidden py-0${archived ? 'border-dashed bg-muted/25' : ''}`}
              >
                {selection ? (
                  <div className="flex min-h-11 items-center border-b px-4">
                    <Checkbox
                      checked={selection.selectedIds.has(rowId)}
                      onCheckedChange={(value) => selection.onToggle(rowId, value === true)}
                      aria-label={`${getTitle(row)} auswählen`}
                    />
                  </div>
                ) : null}
                <div className="space-y-3 px-4 py-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                      <Link
                        to={getHref(row)}
                        className={`text-base font-semibold text-primary hover:underline${archived ? 'text-muted-foreground' : ''}`}
                      >
                        {getTitle(row)}
                      </Link>
                      {titleExtra}
                    </div>
                    {status !== undefined ? <MaintenanceStatusBadge status={status} /> : null}
                  </div>
                  <dl className="grid grid-cols-[minmax(5.5rem,auto)_1fr] gap-x-3 gap-y-1.5 text-sm">
                    {columns.map((column) => (
                      <div key={column.id} className="col-span-2 grid grid-cols-subgrid">
                        <dt className="text-muted-foreground">{column.header}</dt>
                        <dd className="min-w-0 break-words">{column.cell(row)}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
                {renderActions ? (
                  <div className="border-t px-4 py-3">{renderActions(row)}</div>
                ) : null}
              </Card>
            </li>
          );
        })}
      </ul>
      <Card className="hidden gap-0 overflow-hidden py-0 md:flex">
        <Table>
          <TableHeader className="bg-muted/40">
            <TableRow>
              {selection ? (
                <TableHead className="w-px">
                  <Checkbox
                    className="relative z-10"
                    checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                    onCheckedChange={(value) => selection.onToggleAll(value === true)}
                    aria-label="Alle auf dieser Seite auswählen"
                  />
                </TableHead>
              ) : null}
              {titleSortKey && sort ? (
                <SortableTableHead label={titleHeader} sortKey={titleSortKey} sort={sort} />
              ) : (
                <TableHead>{titleHeader}</TableHead>
              )}
              {columns.map((column) =>
                column.sortKey && sort ? (
                  <SortableTableHead
                    key={column.id}
                    label={column.header}
                    sortKey={column.sortKey}
                    sort={sort}
                  />
                ) : (
                  <TableHead key={column.id}>{column.header}</TableHead>
                ),
              )}
              {getStatus ? (
                statusSortKey && sort ? (
                  <SortableTableHead label="Status" sortKey={statusSortKey} sort={sort} />
                ) : (
                  <TableHead>Status</TableHead>
                )
              ) : null}
              {renderActions ? <TableHead className="w-px" /> : null}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => {
              const status = getStatus?.(row);
              const rowId = getRowId(row);
              const archived = isRowArchived?.(row) === true;
              const titleExtra = renderTitleExtra?.(row);
              return (
                <TableRow key={rowId} className={`relative${archived ? 'bg-muted/30' : ''}`}>
                  {selection ? (
                    <TableCell className="relative z-10">
                      <Checkbox
                        checked={selection.selectedIds.has(rowId)}
                        onCheckedChange={(value) => selection.onToggle(rowId, value === true)}
                        aria-label={`${getTitle(row)} auswählen`}
                      />
                    </TableCell>
                  ) : null}
                  <TableCell>
                    <div className="flex flex-wrap items-center gap-2">
                      <Link
                        to={getHref(row)}
                        className={`font-medium text-primary after:absolute after:inset-0 hover:underline${archived ? 'text-muted-foreground' : ''}`}
                      >
                        {getTitle(row)}
                      </Link>
                      {titleExtra ? <span className="relative z-10">{titleExtra}</span> : null}
                    </div>
                  </TableCell>
                  {columns.map((column) => (
                    <TableCell key={column.id}>{column.cell(row)}</TableCell>
                  ))}
                  {getStatus ? (
                    <TableCell>
                      {status !== undefined ? <MaintenanceStatusBadge status={status} /> : null}
                    </TableCell>
                  ) : null}
                  {renderActions ? (
                    <TableCell className="relative z-10">{renderActions(row)}</TableCell>
                  ) : null}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
